import { useCallback, useEffect, useState } from 'react'
import { UpsellFlow, CartTarget } from '../data/upsellFlows'
import { checkoutUrl } from '../data/product'
import { trackCustom, goToShopifyCart } from '../utils/tracking'

/**
 * Runs one negotiation flow at a time. Accepting replaces the order with the
 * accepted product (never stacked); declining moves to the next step, and after
 * the last step sends the customer to exactly what they originally picked.
 */
export function useUpsellFlow() {
  const [flow, setFlow] = useState<UpsellFlow | null>(null)
  const [index, setIndex] = useState(0)
  const [routing, setRouting] = useState(false)

  const step = flow ? flow.steps[index] ?? null : null

  useEffect(() => {
    if (step?.events) trackCustom(step.events.viewed, { total: step.upgrade.total })
  }, [step])

  // Coming back from Shopify with the browser's back button restores this page
  // from cache; reset so the popup is not stuck on "Taking you to checkout".
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRouting(false)
        setFlow(null)
        setIndex(0)
      }
    }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  const route = useCallback((target: CartTarget) => {
    setRouting(true)
    goToShopifyCart(checkoutUrl(target.variantId, target.quantity), {
      content_name: target.name,
      content_ids: [target.variantId],
      quantity: target.quantity,
      // Order total for this cart line.
      value: Number(target.value.toFixed(2)),
    })
  }, [])

  const start = useCallback((next: UpsellFlow) => {
    setRouting(false)
    setIndex(0)
    setFlow(next)
  }, [])

  const accept = useCallback(() => {
    if (!flow || !step) return
    if (step.events) trackCustom(step.events.accepted, { total: step.upgrade.total })
    route(step.target)
  }, [flow, step, route])

  const decline = useCallback(() => {
    if (!flow || !step) return
    if (step.events) trackCustom(step.events.declined, { total: step.current.total })
    if (index < flow.steps.length - 1) {
      setIndex(index + 1)
      return
    }
    route(flow.fallback)
  }, [flow, step, index, route])

  const close = useCallback(() => {
    if (routing) return
    setFlow(null)
    setIndex(0)
  }, [routing])

  return { step, routing, start, accept, decline, close }
}
